/**
 * The markup options (rating / ownership / authority) and the rules that decide which of them a
 * URL can take. Shared by the Markup dialog (MarkupEditorDialog) and the People-Also-Ask answer
 * dialog's source chips (DM-10271), so both offer exactly the same choices.
 *
 * `markup` is a result's markup as /timeline sends it: { rating, owned, auth, ratingHistory,
 * ownershipSource }.
 */

export const UNMARKED = { ownership: "none", authority: "unmarked", rating: -4 };

// result types whose authority is never edited
export const HIDE_AUTHORITY_TYPES = ["newscontainernews"];

export const RATING_OPTIONS = [
  { name: "Positive", value: 1, color: "#86AB6E", fill: "var(--rt-pos-bg)" },
  { name: "Neutral", value: 0, color: "#8D8DA0", fill: "var(--rt-soso-bg)" },
  { name: "Negative", value: -1, color: "#B63A31", fill: "var(--rt-neg-bg)" },
  { name: "Unmarked", value: UNMARKED.rating, color: "#ccc" },
];

export const OWNERSHIP_OPTIONS = [
  { name: "Owned Page", value: "ownedpage", color: "#2c5da6", fill: "var(--own-page-bg)" },
  { name: "Not Owned", value: "notowned", color: "#da7f42", fill: "var(--own-not-owned-bg)" },
  { name: "Not Me", value: "resembler", color: "#A98D71", fill: "var(--own-resembler-bg)" },
  { name: "Owned Domain", value: "owneddomain", color: "#2c5da6", fill: "var(--own-domain-bg)" },
  { name: "Unmarked", value: UNMARKED.ownership, color: "#ccc" },
];

export const AUTHORITY_OPTIONS = [
  { name: "Crowd", value: "crowd", color: "#24a2dc", fill: "var(--ath-crowd-bg)" },
  { name: "Expert", value: "expert", color: "#a7b1e9", fill: "var(--ath-expert-bg)" },
  { name: "Social", value: "social", color: "#3f7e55", fill: "var(--ath-social-bg)" },
  { name: "Wikipedia", value: "wikipedia", color: "#bf956f", fill: "var(--ath-wiki-bg)" },
  { name: "iAuthority", value: "iauthority", color: "#613250", fill: "var(--ath-iauth-bg)" },
  { name: "Unmarked", value: UNMARKED.authority, color: "#ccc" },
];

// `fill`: the colour a result row takes for that value (the timeline's markup fills, _colors.scss).

/** The current value of each markup type, Unmarked when the URL has none. */
export function markupValues(markup = {}) {
  return {
    rating: markup.rating || markup.rating === 0 ? markup.rating : UNMARKED.rating,
    ownership: markup.owned || UNMARKED.ownership,
    authority: markup.auth || UNMARKED.authority,
  };
}

export function numRatings(markup = {}) {
  return markup.ratingHistory ? Object.keys(markup.ratingHistory).length : 0;
}

/** [{ name, options, value }] for rating, ownership and authority. */
export function markupTypes(markup = {}) {
  const values = markupValues(markup);
  return [
    { name: "rating", options: RATING_OPTIONS, value: values.rating },
    { name: "ownership", options: OWNERSHIP_OPTIONS, value: values.ownership },
    { name: "authority", options: AUTHORITY_OPTIONS, value: values.authority },
  ];
}

/**
 * A rating option asks Mark all / Mark forward / Mark backward when the URL already has a
 * rating history (setting it "all" would erase the history).
 */
export function usesScope(typeName, option, markup = {}) {
  return typeName === "rating" && option.value !== UNMARKED.rating && numRatings(markup) > 0;
}

/**
 * A markup type is shown when the toolbar has it enabled; authority only to superusers, and
 * never for HIDE_AUTHORITY_TYPES.
 */
export function isGroupVisible(typeName, { isMarkupEnabled, isSu, resultType }) {
  if (!isMarkupEnabled(typeName)) {
    return false;
  }
  if (typeName !== "authority") {
    return true;
  }
  if (!isSu) {
    return false;
  }
  return !HIDE_AUTHORITY_TYPES.includes(resultType);
}

/** Unmarking an ownership inherited from a marked social page would delete that page's marking. */
export function isUnmarkInheritedOwnership(markup, typeName, option) {
  return !!markup.ownershipSource && typeName === "ownership" && option.value === UNMARKED.ownership;
}

export function buttonTitle({ url, markup }, typeName, option) {
  if (url && isUnmarkInheritedOwnership(markup, typeName, option)) {
    return `This status comes from the marking on ${markup.ownershipSource}. To unmark it, unmark that URL.`;
  }
  return undefined;
}

/**
 * { disabled: true }, { hidden: true } or {} for one option of one markup type of a URL.
 * `target` = { url, markup, resultType }.
 */
export function buttonAvailability({ url, markup = {}, resultType }, typeName, option) {
  const { rating, ownership, authority } = markupValues(markup);
  const isButton = (...values) => values.includes(option.value);
  const isButtonOfType = (type) => typeName === type;
  const isPressed = (value, ...values) => values.includes(value);

  if (!url) {
    return {};
  }

  // disable these because clicking them won't change anything
  if (
    isButton(authority, ownership) ||
    (isButton(rating) && isPressed(rating, UNMARKED.rating))
  ) {
    return { disabled: true };
  }

  if (isButton("wikipedia") && resultType === "videocontainervideo") {
    return { hidden: true };
  }

  if (isButton("wikipedia", "iauthority")) {
    return { disabled: true };
  }

  if (
    isPressed(authority, "wikipedia") &&
    (isButton("ownedpage", "owneddomain") || isButtonOfType("authority"))
  ) {
    return { disabled: true };
  }

  if (isPressed(authority, "crowd", "expert", "social") && isButton("owneddomain")) {
    return { disabled: true };
  }

  // Unmarked would delete the profile's marking along with every post under it
  if (isUnmarkInheritedOwnership(markup, typeName, option)) {
    return { disabled: true };
  }

  if (isPressed(ownership, "ownedpage") && isButtonOfType("rating")) {
    return { disabled: true };
  }

  if (isPressed(ownership, "owneddomain")) {
    if (
      isButtonOfType("rating") ||
      isButtonOfType("authority") ||
      (isButtonOfType("ownership") && !isButton(UNMARKED.ownership))
    ) {
      return { disabled: true };
    }
  }

  return {};
}

/** Inline colours of an option button: filled in its colour when it is the current value. */
export function buttonColorStyles(option, currentValue) {
  const isSelected = option.value === currentValue;
  return {
    "border-color": option.color,
    "background-color": isSelected ? option.color : "white",
    color: isSelected ? "white" : undefined,
    // so we can disable the selected button but it doesn't look pale
    opacity: isSelected ? 1 : undefined,
  };
}

/** Whether a value is "Unmarked" (each markup type has its own unmarked value). */
export function isUnmarkedValue(value) {
  return value === UNMARKED.rating || value === UNMARKED.ownership || value === UNMARKED.authority;
}

/** True when no option of the type can be picked (e.g. an owned domain's rating). */
export function isLocked(target, typeName, options) {
  return options.every((option) => buttonAvailability(target, typeName, option).disabled);
}

/** A url's row fill for the toolbar's markup mode ('rating' | 'ownership' | 'authority'); white when unmarked. */
export function markupFill(markup = {}, mode = "rating") {
  const type = markupTypes(markup).find((t) => t.name === mode) || markupTypes(markup)[0];
  return type.options.find((o) => o.value === type.value)?.fill || "#fff";
}
